import type { $Fetch } from 'ofetch';
import type { UnidadeItemGateway } from '@/application/port/out/UnidadeItemGateway';
import type {
  CreateUnidadeItemCommand, CreateUnidadeItemDaBibliotecaCommand,
  CreateUnidadeItemPdfCommand, UnidadeItem,
} from '@/domain/model/UnidadeItem';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** Gateway HTTP dos itens de uma Unidade — BE `/api/v1/unidades/{id}/itens`. */
export class HttpUnidadeItemGateway implements UnidadeItemGateway {
  constructor(private readonly client: $Fetch) {}

  private base(unidadeId: string): string {
    return `/api/v1/unidades/${encodeURIComponent(unidadeId)}/itens`;
  }

  async list(unidadeId: string): Promise<UnidadeItem[]> {
    const res = await this.client<ApiResponse<UnidadeItem[]>>(this.base(unidadeId), { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(unidadeId: string, cmd: CreateUnidadeItemCommand): Promise<UnidadeItem> {
    const res = await this.client<ApiResponse<UnidadeItem>>(this.base(unidadeId), { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async createPdf(unidadeId: string, cmd: CreateUnidadeItemPdfCommand): Promise<UnidadeItem> {
    const form = new FormData();
    form.append('titulo', cmd.titulo);
    form.append('ordem', String(cmd.ordem));
    form.append('arquivo', cmd.arquivo);
    const res = await this.client<ApiResponse<UnidadeItem>>(`${this.base(unidadeId)}/pdf`, { method: 'POST', body: form });
    assertApiSuccess(res);
    return res.data;
  }

  async createDaBiblioteca(unidadeId: string, cmd: CreateUnidadeItemDaBibliotecaCommand): Promise<UnidadeItem> {
    const res = await this.client<ApiResponse<UnidadeItem>>(`${this.base(unidadeId)}/da-biblioteca`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async arquivar(unidadeId: string, itemId: string): Promise<void> {
    await this.client<void>(`${this.base(unidadeId)}/${encodeURIComponent(itemId)}`, { method: 'DELETE' });
  }

  async reordenar(unidadeId: string, idsNaOrdem: string[]): Promise<void> {
    await this.client<void>(`${this.base(unidadeId)}/reordenar`, { method: 'PUT', body: idsNaOrdem });
  }

  async baixar(unidadeId: string, itemId: string): Promise<Blob> {
    return await this.client(`${this.base(unidadeId)}/${encodeURIComponent(itemId)}/download`, { responseType: 'blob' });
  }
}
